import { PHASE } from "../../common/constants.ts";
import { idb } from "../db/index.ts";
import g from "./g.ts";
import helpers from "./helpers.ts";
import type { TeamFiltered } from "../../common/types.ts";
import advStatsSave from "./advStatsSave.ts";
import { NUM_OUTS_PER_GAME } from "../../common/processPlayerStats.baseball.ts";
import { POS_NUMBERS_INVERSE } from "../../common/constants.baseball.ts";
import { groupByUnique, range } from "../../common/utils.ts";
import statsRowIsCurrent from "../core/player/statsRowIsCurrent.ts";
import { defaultGameAttributes } from "../../common/defaultGameAttributes.ts";

const teamStats = [
	"h",
	"2b",
	"3b",
	"hr",
	"bb",
	"hbp",
	"ab",
	"er",
	"outs",
	"gp",
	"po",
	"a",
	"e",
	"dp",
	"poTot",
	"soPit",
	"pts",
	// For the V20 fielding runs
	"bf",
	"bbPit",
	"hbpPit",
	"hrPit",
	"outsF",
	"pb",
	"sbF",
	"csF",
] as const;

type Team = TeamFiltered<["tid"], ["ptsDefault"], typeof teamStats, number>;

const POSITIONAL_ADJUSTMENT_COEFFICIENTS = {
	C: 12.5,
	"1B": -12.5,
	"2B": 2.5,
	"3B": 2.5,
	SS: 7.5,
	LF: -7.5,
	CF: 2.5,
	RF: -7.5,
	DH: -17.5,
	P: 5,
};

const frP = (i: 0, t: Team, apl: number) => {
	const po = t.stats.po[i] ?? 0;
	const a = t.stats.a[i] ?? 0;
	const e = t.stats.e[i] ?? 0;
	const dp = t.stats.dp[i] ?? 0;
	const fr =
		0.1 * (po + 2 * a - e + dp) - apl * (t.stats.poTot - t.stats.soPit);
	if (Number.isNaN(fr)) {
		return 0;
	}
	return fr;
};

const frC = (i: 1, t: Team, apl: number) => {
	const po = t.stats.po[i] ?? 0;
	const a = t.stats.a[i] ?? 0;
	const e = t.stats.e[i] ?? 0;
	const dp = t.stats.dp[i] ?? 0;

	const fr =
		0.2 * (po - t.stats.soPit + 2 * a - e + dp) -
		apl * (t.stats.poTot - t.stats.soPit);
	if (Number.isNaN(fr)) {
		return 0;
	}
	return fr;
};

const fr1B = (i: 2, t: Team, apl: number) => {
	const a = t.stats.a[i] ?? 0;
	const e = t.stats.e[i] ?? 0;

	const fr = 0.2 * (2 * a - e) - apl * (t.stats.poTot - t.stats.soPit);
	if (Number.isNaN(fr)) {
		return 0;
	}
	return fr;
};

const fr2B = (i: 3 | 4 | 5, t: Team, apl: number) => {
	const po = t.stats.po[i] ?? 0;
	const a = t.stats.a[i] ?? 0;
	const e = t.stats.e[i] ?? 0;
	const dp = t.stats.dp[i] ?? 0;

	const fr =
		0.2 * (po + 2 * a - e + dp) - apl * (t.stats.poTot - t.stats.soPit);
	if (Number.isNaN(fr)) {
		return 0;
	}
	return fr;
};

const frLF = (i: 6 | 7 | 8, t: Team, apl: number) => {
	const po = t.stats.po[i] ?? 0;
	const a = t.stats.a[i] ?? 0;
	const e = t.stats.e[i] ?? 0;
	const dp = t.stats.dp[i] ?? 0;

	const fr =
		0.2 * (po + 4 * a - e + 2 * dp) - apl * (t.stats.poTot - t.stats.soPit);
	if (Number.isNaN(fr)) {
		return 0;
	}
	return fr;
};

const aplP = (i: 0, league: any) => {
	const apl =
		(0.1 * (league.po[i] + 2 * league.a[i] - league.e[i] + league.dp[i])) /
		(league.poTot - league.soPit);
	if (Number.isNaN(apl)) {
		return 0;
	}
	return apl;
};

const aplC = (i: 1, league: any) => {
	const apl =
		(0.2 *
			(league.po[i] -
				league.soPit +
				2 * league.a[i] -
				league.e[i] +
				league.dp[i])) /
		(league.poTot - league.soPit);
	if (Number.isNaN(apl)) {
		return 0;
	}
	return apl;
};

const apl1B = (i: 2, league: any) => {
	const apl =
		(0.2 * (2 * league.a[i] - league.e[i])) / (league.poTot - league.soPit);
	if (Number.isNaN(apl)) {
		return 0;
	}
	return apl;
};

const apl2B = (i: 3 | 4 | 5, league: any) => {
	const apl =
		(0.2 * (league.po[i] + 2 * league.a[i] - league.e[i] + league.dp[i])) /
		(league.poTot - league.soPit);
	if (Number.isNaN(apl)) {
		return 0;
	}
	return apl;
};

const aplLF = (i: 6 | 7 | 8, league: any) => {
	const apl =
		(0.2 * (league.po[i] + 4 * league.a[i] - league.e[i] + 2 * league.dp[i])) /
		(league.poTot - league.soPit);
	if (Number.isNaN(apl)) {
		return 0;
	}
	return apl;
};

// Fielding runs, V20 (the Gui's formula). Opportunity comes from the team's real balls in play,
// each position is compared with a robust benchmark built from the other teams, small samples
// are shrunk, extremes are soft capped and every position is recentered on zero. Catchers are
// judged separately on the running game, passed balls and errors. Ratings are never used.
const V20_WEIGHTS: Record<string, [number, number]> = {
	// [wPO, wA]
	P: [1, 1],
	"1B": [0.15, 0.8],
	"2B": [0.2, 1],
	"3B": [0.2, 1],
	SS: [0.2, 1],
	LF: [1, 0],
	CF: [1, 0],
	RF: [1, 0],
};
// Run value of one defensive play, the same 0.2 scale the classic formula uses per play
const V20_RV = 0.2;
const V20_RELIABILITY_OUTS = 400;
const V20_SCALE = 1.5;
const V20_CAP = 22;
const V20_CAP_C = 12;
const V20_CAP_OUTS = 4300;
const CATCHER_INDEX = 1;
// With fielding chances (balls hit toward each fielder), runs come straight from the plays: a ball kept from
// being a hit is worth about 0.75 runs, an error on a ball he got to about 0.5
const CHANCE_HIT_RUNS = 0.75;
const CHANCE_ERROR_RUNS = 0.5;
const sumArray = (values: (number | undefined)[] | undefined) =>
	(values ?? []).reduce<number>((sum, value) => sum + (value ?? 0), 0);

type V20Team = {
	tid: number;
	stats: Record<string, any>;
};
type V20Player = {
	tid: number;
	stats: Record<string, any>;
};

const teamBIP = (t: V20Team) =>
	(t.stats.bf ?? 0) -
	(t.stats.soPit ?? 0) -
	(t.stats.bbPit ?? 0) -
	(t.stats.hbpPit ?? 0) -
	(t.stats.hrPit ?? 0);

const opportunity = (t: V20Team, outsF: number) =>
	t.stats.outs > 0 ? (teamBIP(t) * outsF) / t.stats.outs : 0;

const percentile = (sorted: number[], q: number) => {
	if (sorted.length === 0) {
		return 0;
	}
	const pos = (sorted.length - 1) * q;
	const lo = Math.floor(pos);
	const hi = Math.ceil(pos);
	return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (pos - lo);
};

// Rates clipped to p5-p95 across teams, then weighted by opportunity, leaving one team out
const robustBenchmark = (
	rows: { tid: number; rate: number; weight: number }[],
	excludeTid: number,
) => {
	const sorted = rows.map((row) => row.rate).sort((a, b) => a - b);
	const p5 = percentile(sorted, 0.05);
	const p95 = percentile(sorted, 0.95);
	let others = rows.filter((row) => row.tid !== excludeTid);
	if (others.length === 0) {
		others = rows;
	}
	let sum = 0;
	let weights = 0;
	for (const row of others) {
		sum += Math.min(p95, Math.max(p5, row.rate)) * row.weight;
		weights += row.weight;
	}
	return weights > 0 ? sum / weights : 0;
};

const softCap = (raw: number, outsF: number, capBase: number) => {
	const cap = capBase * Math.sqrt(outsF / V20_CAP_OUTS);
	return cap > 0 ? cap * Math.tanh(raw / cap) : 0;
};

const reliability = (outsF: number) => outsF / (outsF + V20_RELIABILITY_OUTS);

export const calculateRfldV20 = (players: V20Player[], teams: V20Team[]) => {
	const out: number[][] = players.map(() => []);
	const teamsByTid = groupByUnique(teams, "tid");

	// Seasons played since chances are recorded compare each fielder with the balls actually hit toward him.
	// Older seasons, or one that started before, estimate his share from the team's balls in play.
	const useChances =
		teams.length > 0 &&
		teams.every((t) => {
			const bip = teamBIP(t);
			return bip > 0 && sumArray(t.stats.chF) >= 0.9 * bip;
		});

	// Team rates per position, for the benchmarks
	const rates: Record<
		number,
		{
			play: { tid: number; rate: number; weight: number }[];
			error: { tid: number; rate: number; weight: number }[];
		}
	> = {};
	for (const [pos, [wPO, wA]] of Object.entries(V20_WEIGHTS)) {
		const j = POS_INDEX[pos]!;
		rates[j] = { play: [], error: [] };
		for (const t of teams) {
			const O = useChances
				? (t.stats.chF?.[j] ?? 0)
				: opportunity(t, t.stats.outsF?.[j] ?? 0);
			if (O <= 0) {
				continue;
			}
			const M = useChances
				? (t.stats.cvF?.[j] ?? 0)
				: wPO * (t.stats.po?.[j] ?? 0) + wA * (t.stats.a?.[j] ?? 0);
			rates[j]!.play.push({ tid: t.tid, rate: M / O, weight: O });
			rates[j]!.error.push({ tid: t.tid, rate: (t.stats.e?.[j] ?? 0) / O, weight: O });
		}
	}
	const benchmarkCache = new Map<string, { play: number; error: number }>();
	const benchmark = (j: number, tid: number) => {
		const key = `${j},${tid}`;
		let value = benchmarkCache.get(key);
		if (!value) {
			value = {
				play: robustBenchmark(rates[j]!.play, tid),
				error: robustBenchmark(rates[j]!.error, tid),
			};
			benchmarkCache.set(key, value);
		}
		return value;
	};

	// Catcher league rates
	let sbF = 0;
	let csF = 0;
	let pb = 0;
	let eC = 0;
	let outsC = 0;
	for (const t of teams) {
		sbF += t.stats.sbF ?? 0;
		csF += t.stats.csF ?? 0;
		pb += t.stats.pb ?? 0;
		eC += t.stats.e?.[CATCHER_INDEX] ?? 0;
		outsC += t.stats.outsF?.[CATCHER_INDEX] ?? 0;
	}
	const leagueCSRate = sbF + csF > 0 ? csF / (sbF + csF) : 0;
	const leaguePBRate = outsC > 0 ? pb / outsC : 0;
	const leagueErrorRateC = outsC > 0 ? eC / outsC : 0;

	for (const [i, p] of players.entries()) {
		const t = teamsByTid[p.tid];
		if (!t) {
			continue;
		}
		for (const [pos, [wPO, wA]] of Object.entries(V20_WEIGHTS)) {
			const j = POS_INDEX[pos]!;
			const outsF = p.stats.outsF?.[j] ?? 0;
			if (!(outsF > 0)) {
				continue;
			}
			const E = p.stats.e?.[j] ?? 0;
			const { play, error } = benchmark(j, t.tid);
			let raw;
			if (useChances) {
				const O = p.stats.chF?.[j] ?? 0;
				const M = p.stats.cvF?.[j] ?? 0;
				const rangeRuns = (M - O * play) * CHANCE_HIT_RUNS;
				const errorRuns = (O * error - E) * CHANCE_ERROR_RUNS;
				raw = (rangeRuns + errorRuns) * reliability(outsF);
			} else {
				const O = opportunity(t, outsF);
				const M = wPO * (p.stats.po?.[j] ?? 0) + wA * (p.stats.a?.[j] ?? 0);
				const rangeRuns = (M - O * play) * V20_RV;
				const errorRuns = (O * error - E) * 2.5 * V20_RV;
				raw = (rangeRuns + errorRuns) * reliability(outsF) * V20_SCALE;
			}
			out[i]![j] = softCap(raw, outsF, V20_CAP);
		}

		const outsFC = p.stats.outsF?.[CATCHER_INDEX] ?? 0;
		if (outsFC > 0) {
			const attempts = (p.stats.sbF ?? 0) + (p.stats.csF ?? 0);
			const catcherRaw =
				((p.stats.csF ?? 0) - attempts * leagueCSRate) * 2.0 * V20_RV +
				(outsFC * leaguePBRate - (p.stats.pb ?? 0)) * 2.5 * V20_RV +
				(outsFC * leagueErrorRateC - (p.stats.e?.[CATCHER_INDEX] ?? 0)) *
					2.5 *
					V20_RV;
			const raw = catcherRaw * reliability(outsFC) * V20_SCALE;
			out[i]![CATCHER_INDEX] = softCap(raw, outsFC, V20_CAP_C);
		}
	}

	// Rfld is accumulated runs, not a rate. Remove the residual total in
	// proportion to defensive outs, so brief appearances receive a small correction.
	// The sum is zero across the player rows participating in this calculation.
	for (const j of [CATCHER_INDEX, ...Object.keys(V20_WEIGHTS).map((pos) => POS_INDEX[pos]!)]) {
		let sum = 0;
		let weights = 0;
		for (const [i, p] of players.entries()) {
			const value = out[i]![j];
			if (value !== undefined) {
				const w = p.stats.outsF?.[j] ?? 0;
				sum += value;
				weights += w;
			}
		}
		if (weights > 0) {
			const runsPerOut = sum / weights;
			for (const [i, row] of out.entries()) {
				if (row[j] !== undefined) {
					row[j]! -= runsPerOut * (players[i]!.stats.outsF?.[j] ?? 0);
				}
			}
		}
	}

	return out;
};

// Position name -> index in the byPos stat arrays (P is 1 in scorekeeping, so index 0)
const POS_INDEX: Record<string, number> = Object.fromEntries(
	Object.entries(POS_NUMBERS_INVERSE).map(([num, pos]) => [pos, Number(num) - 1]),
);

// The classic formula, kept only to compare against V20 in tests
export const calculateRfldClassic = (
	players: any[],
	teams: Team[],
	league: any,
) => calculateWAR(players, teams, league, "classic").rfld;

// Hendela, Karl (2020) "Sabermetric Analysis: Wins-Above-Replacement," Locus: The Seton Hall Journal of Undergraduate Research: Vol. 3 , Article 7.
// Available at: https://scholarship.shu.edu/locus/vol3/iss1/7
export const calculateWAR = (
	players: any[],
	teams: Team[],
	league: any,
	fieldingFormula: "v20" | "classic" = "v20",
) => {
	const rfldV20 =
		fieldingFormula === "v20" ? calculateRfldV20(players, teams) : undefined;
	const rbat = []; // Batting Runs
	const rbr = []; // Baserunning Runs
	const rfld: number[][] = []; // Fielding Runs
	const rpos = []; // Positional Adjustment Runs
	const rpit = []; // Pitching Runs Saved
	const raa = []; // Runs Above Average
	const waa = []; // Wins Above Average
	const rrep = []; // Runs From Replacement Level
	const rar = []; // Runs Above Replacement
	const war = []; // Wins Above Replacement

	const lgf = 1; // "league factor"

	const runsPerGame = league.pts / league.gp;

	const abf =
		(0.47 * league.h +
			0.38 * league["2b"] +
			0.55 * league["3b"] +
			0.93 * league.hr +
			0.33 * (league.bb + league.hbp)) /
		(league.ab - lgf * league.h);

	const apl = {
		P: aplP(0, league),
		C: aplC(1, league),
		"1B": apl1B(2, league),
		"2B": apl2B(3, league),
		"3B": apl2B(4, league),
		SS: apl2B(5, league),
		LF: aplLF(6, league),
		CF: aplLF(7, league),
		RF: aplLF(8, league),
	};

	const teamFieldingRuns: Record<
		number,
		{
			P: number;
			C: number;
			"1B": number;
			"2B": number;
			"3B": number;
			SS: number;
			LF: number;
			CF: number;
			RF: number;
		}
	> = {};

	for (const t of teams) {
		teamFieldingRuns[t.tid] = {
			P: frP(0, t, apl.P),
			C: frC(1, t, apl.C),
			"1B": fr1B(2, t, apl["1B"]),
			"2B": fr2B(3, t, apl["2B"]),
			"3B": fr2B(4, t, apl["3B"]),
			SS: fr2B(5, t, apl.SS),
			LF: frLF(6, t, apl.LF),
			CF: frLF(7, t, apl.CF),
			RF: frLF(8, t, apl.RF),
		};
	}
	const teamsByTid = groupByUnique(teams, "tid");

	// This is for scaling POSITIONAL_ADJUSTMENT_COEFFICIENTS, which are defined relative to a normal 162 game schedule
	const numGames = defaultGameAttributes.numGames[0].value;

	for (const [i, p] of players.entries()) {
		const t = teamsByTid[p.tid]!;

		// Batting Runs
		const pitcherFactor = p.stats.gpPit >= 0.75 * p.stats.gp ? 0.5 : 1;
		rbat[i] =
			0.47 * p.stats.h +
			0.38 * p.stats["2b"] +
			0.55 * p.stats["3b"] +
			0.93 * p.stats.hr +
			0.33 * (p.stats.bb + p.stats.hbp) -
			pitcherFactor * abf * (p.stats.ab - p.stats.h);

		// Baserunning Runs
		rbr[i] = 0.3 * p.stats.sb - 0.6 * p.stats.cs;

		rfld[i] = [];
		rpos[i] = 0;
		for (let j = 0; j < p.stats.gpF.length; j++) {
			const gpF = p.stats.gpF[j];
			if (gpF !== undefined && gpF > 0) {
				const pos =
					POS_NUMBERS_INVERSE[(j + 1) as keyof typeof POS_NUMBERS_INVERSE];

				// Positional Adjustment Runs
				rpos[i]! += (gpF / numGames) * POSITIONAL_ADJUSTMENT_COEFFICIENTS[pos];

				// Fielding Runs
				if (rfldV20) {
					if (rfldV20[i]![j] !== undefined) {
						rfld[i]![j] = rfldV20[i]![j]!;
					}
				} else if (pos !== "DH") {
					const po = pos === "C" ? p.stats.po[j] - p.stats.poSo : p.stats.po[j];
					if (po !== undefined && po > 0) {
						const poTeam =
							pos === "C" ? t.stats.po[j]! - t.stats.soPit : t.stats.po[j]!;
						rfld[i]![j] =
							helpers.ratio(po, poTeam) * teamFieldingRuns[t.tid]![pos];
					}
				}
			}
		}

		// Pitching Runs Saved
		rpit[i] = (p.stats.outs / NUM_OUTS_PER_GAME) * league.era - p.stats.er;

		// Replacement Player Adjustment
		if (p.stats.gpPit > 0) {
			rrep[i] = p.stats.bf / 50;
		} else {
			rrep[i] = p.stats.pa / 30;
		}

		raa[i] = rbat[i]! + rbr[i]! + helpers.sum(rfld[i]!) + rpos[i]! + rpit[i]!;

		waa[i] = raa[i]! / runsPerGame;

		rar[i] = raa[i]! + rrep[i]!;

		// Wins Above Replacement
		war[i] = rar[i]! / runsPerGame;
	}

	return {
		rbat,
		rbr,
		rfld,
		rpos,
		rpit,
		raa,
		waa,
		rrep,
		rar,
		war,
	};
};

const advStats = async () => {
	const playoffs = PHASE.PLAYOFFS === g.get("phase");

	const playersRaw = await idb.cache.players.indexGetAll("playersByTid", [
		0, // Active players have tid >= 0
		Infinity,
	]);
	const players = (
		await idb.getCopies.playersPlus(playersRaw, {
			attrs: ["pid", "tid"],
			stats: [
				"h",
				"2b",
				"3b",
				"hr",
				"bb",
				"hbp",
				"ab",
				"sb",
				"cs",
				"gpF",
				"po",
				"poSo",
				// V20 fielding runs
				"outsF",
				"a",
				"e",
				"pb",
				"sbF",
				"csF",
				"outs",
				"er",
				"bf",
				"pa",
				"gp",
				"gpPit",

				// For statsRowIsCurrenet
				"tid",
				"season",
				"playoffs",
			],
			season: g.get("season"),
			playoffs,
			regularSeason: !playoffs,
			// Read only below; skipping the copy of every active player's rows matters because this runs every day
			copyStatsRows: false,
		})
	).filter((p) => {
		// Ignore players with no stats row, such as players signed/traded who haven't played a game yet, since we don't call addStatsRow when joining the roster now
		return statsRowIsCurrent(p.stats, p.tid, playoffs);
	});

	const teams = await idb.getCopies.teamsPlus(
		{
			attrs: ["tid"],
			stats: teamStats,
			seasonAttrs: ["ptsDefault"],
			season: g.get("season"),
			playoffs,
			regularSeason: !playoffs,
			addDummySeason: true,
			active: true,
		},
		"noCopyCache",
	);
	const league: any = teams.reduce((memo: any, t) => {
		for (const key of teamStats) {
			if (Array.isArray(t.stats[key])) {
				if (!memo[key]) {
					memo[key] = range(9);
				}

				for (let i = 0; i < (t.stats[key] as number[]).length; i++) {
					const value = (t.stats[key] as number[])[i];
					if (value !== undefined) {
						memo[key][i] += value;
					}
				}
			} else {
				if (Object.hasOwn(memo, key)) {
					memo[key] += t.stats[key];
				} else {
					memo[key] = t.stats[key];
				}
			}
		}

		return memo;
	}, {});
	league.gp /= 2;
	league.era = helpers.ratio(league.er, league.outs / NUM_OUTS_PER_GAME);

	const updatedStats = { ...calculateWAR(players, teams, league) };
	await advStatsSave(players, playersRaw, updatedStats);
};

export default advStats;
